'use client';

import { useEffect } from 'react';
import { getCookie, hasCookie, setCookie } from 'cookies-next';
import { find, get, isEmpty, map } from 'lodash';
import { ChevronDownIcon, DoneIcon, WorldIcon } from '@/icons';
import { useLocaleStore } from '@/stores/localeStore';
import { cn } from '@/utils/mergeClassName';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { LOCALE_KEY, longLocaleOptions, shortLocaleOptions } from './constants';
import type { Locales } from './types';

type LocaleButtonProps = {
  dropdownSide?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
  shortOptions?: boolean;
  styles?: {
    content?: string;
    item?: string;
  };
  scrollToBottom?: boolean;
};

export const LocaleButton = ({
  dropdownSide,
  className,
  styles,
  shortOptions = false,
  scrollToBottom = false
}: LocaleButtonProps) => {
  const locale = useLocaleStore((state) => state.locale);
  const changeLocale = useLocaleStore((state) => state.changeLocale);

  const localeOptions = shortOptions ? shortLocaleOptions : longLocaleOptions;

  useEffect(() => {
    const currentLocale = hasCookie(LOCALE_KEY) ? getCookie(LOCALE_KEY)?.trim() : 'pt-BR';

    if (!isEmpty(find(localeOptions, { value: currentLocale }))) {
      changeLocale(currentLocale as Locales);
    }
  }, [localeOptions, changeLocale]);

  const handleChangeLocale = (newLocale: string) => {
    setCookie(LOCALE_KEY, newLocale, { maxAge: 60 * 60 * 24 * 7 }); // 7 days
    changeLocale(newLocale as Locales);
  };

  /** Bugfix Safari bottom dropdown */
  const handleScrollToBottom = (open: boolean) => {
    if (scrollToBottom && open) {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'instant'
      });
    }
  };

  return (
    <DropdownMenu.Root onOpenChange={handleScrollToBottom}>
      <DropdownMenu.Trigger asChild>
        <button
          className={cn(
            'group flex h-12 items-center gap-2 py-2 text-xs font-semibold uppercase tracking-[1px] text-white outline-none focus:shadow-[0_0_0_2px]',
            className
          )}
          aria-label="Selecionar idioma"
        >
          <WorldIcon />
          {find(localeOptions, { value: locale })?.label}

          <span
            className="ease-[cubic-bezier(0.87,_0,_0.13,_1)] text-white transition-transform duration-100 group-data-[state=open]:rotate-180"
            aria-hidden
          >
            <ChevronDownIcon width={16} height={16} />
          </span>
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={cn(
            'bg-white p-2 shadow shadow-grey-300 will-change-[opacity,transform] data-[side=bottom]:animate-slide-up-and-fade data-[side=left]:animate-slide-right-and-fade data-[side=right]:animate-slide-left-and-fade data-[side=top]:animate-slide-down-and-fade',
            get(styles, 'content')
          )}
          sideOffset={4}
          side={dropdownSide}
        >
          <DropdownMenu.RadioGroup
            value={locale}
            onValueChange={(value) => handleChangeLocale(value)}
          >
            {map(localeOptions, (localeOption) => (
              <DropdownMenu.RadioItem
                key={localeOption.value}
                className={cn(
                  'relative flex select-none items-center p-2 pl-6 text-sm leading-none text-grey-700 outline-none data-[disabled]:pointer-events-none data-[highlighted]:bg-grey-500 data-[highlighted]:text-white',
                  get(styles, 'item')
                )}
                value={localeOption.value}
              >
                <DropdownMenu.ItemIndicator className="absolute left-1 inline-flex size-4 items-center justify-center">
                  <DoneIcon />
                </DropdownMenu.ItemIndicator>
                {localeOption.label}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>

          <DropdownMenu.Arrow className="fill-white" />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
